import {escapeHtml as escapePrintValue} from "../../utils/escapeHtml";
import React, { useMemo } from "react";

import {
  formatMoney,
  toAmount,
} from "../../services/billingService";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatDateTime = (value) => {
  if (!value) return "-";

  let date;

  if (value?.toDate instanceof Function) {
    date = value.toDate();
  } else if (value?.seconds) {
    date = new Date(value.seconds * 1000);
  } else {
    date = new Date(value);
  }

  if (
    !date ||
    Number.isNaN(date.getTime())
  ) {
    return clean(value) || "-";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (value) => {
  if (!value) return "-";

  let date;

  if (value?.toDate instanceof Function) {
    date = value.toDate();
  } else if (value?.seconds) {
    date = new Date(value.seconds * 1000);
  } else {
    date = new Date(value);
  }

  if (
    !date ||
    Number.isNaN(date.getTime())
  ) {
    return clean(value) || "-";
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getItems = (stay, key) =>
  Array.isArray(stay?.[key])
    ? stay[key]
    : [];

const getItemAmount = (item) => {
  if (
    item?.amount !== undefined &&
    item?.amount !== null
  ) {
    return toAmount(item.amount);
  }

  return (
    toAmount(item?.quantity || 1) *
    toAmount(item?.rate)
  );
};

const getPrimaryGuest = (stay) => {
  if (
    Array.isArray(stay?.guests) &&
    stay.guests.length
  ) {
    return (
      stay.guests.find(
        (guest) =>
          guest?.isPrimary === true
      ) ||
      stay.guests[0]
    );
  }

  return {
    name: stay?.guestName || "",
    phoneNumber:
      stay?.phoneNumber || "",
    address: stay?.address || "",
    nationality:
      stay?.nationality || "",
    idType: stay?.idType || "",
    idNumber: stay?.idNumber || "",
  };
};

const getBookingReference = (stay) =>
  clean(
    stay?.otaBookingId ||
      stay?.corporateReference ||
      stay?.bookingReference ||
      stay?.bookingId
  ) || "-";

const getPaymentStatus = (bill) => {
  if (toAmount(bill?.balance) <= 0) {
    return {
      text: "PAID",
      background: "#dcfce7",
      color: "#166534",
    };
  }

  if (toAmount(bill?.paid) > 0) {
    return {
      text: "PARTIAL",
      background: "#fef3c7",
      color: "#92400e",
    };
  }

  return {
    text: "PENDING",
    background: "#fee2e2",
    color: "#b91c1c",
  };
};

const getStayDuration = (stay) => {
  if (stay?.stayType) {
    return stay.stayType;
  }

  if (
    !stay?.checkInAt ||
    !stay?.expectedCheckOutAt
  ) {
    return "-";
  }

  const start = new Date(stay.checkInAt);
  const end = new Date(
    stay.expectedCheckOutAt
  );

  if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime())
  ) {
    return "-";
  }

  const hours = Math.max(
    0,
    Math.round(
      ((end - start) /
        (1000 * 60 * 60)) *
        10
    ) / 10
  );

  return `${hours} Hours`;
};

/* -------------------------------------------------------------------------- */
/*                            Print HTML Helpers                              */
/* -------------------------------------------------------------------------- */

const buildChargeRowsHtml = ({
  title,
  items,
}) => {
  if (!items.length) return "";

  return `
    <tr class="category-row">
      <td colspan="5">${escapeHtml(title)}</td>
    </tr>

    ${items
      .map(
        (item, index) => `
          <tr>
            <td>${index + 1}</td>

            <td>
              ${escapeHtml(
                item?.title ||
                  item?.name ||
                  title
              )}

              ${
                escapePrintValue(clean(item?.note))
                  ? `<div class="item-note">${escapeHtml(
                      item.note
                    )}</div>`
                  : ""
              }
            </td>

            <td class="right">
              ${escapeHtml(
                item?.quantity || 1
              )}
            </td>

            <td class="right">
              ${escapeHtml(
                formatMoney(
                  item?.rate ??
                    getItemAmount(item)
                )
              )}
            </td>

            <td class="right">
              ${escapeHtml(
                formatMoney(
                  getItemAmount(item)
                )
              )}
            </td>
          </tr>
        `
      )
      .join("")}
  `;
};

const buildInvoiceHtml = ({
  stay,
  bill,
  hotelName,
  invoiceNumber,
  invoiceDate,
}) => {
  const primaryGuest =
    getPrimaryGuest(stay);

  const foodItems = getItems(
    stay,
    "foodItems"
  );

  const laundryItems = getItems(
    stay,
    "laundryItems"
  );

  const extraItems = getItems(
    stay,
    "extraItems"
  );

  const payments = getItems(
    stay,
    "payments"
  );

  const paymentStatus =
    getPaymentStatus(bill);

  const chargeRows = [
    `
      <tr>
        <td>1</td>

        <td>
          Room Tariff

          ${
            escapePrintValue(clean(stay?.stayType))
              ? `<div class="item-note">${escapeHtml(
                  stay.stayType
                )}</div>`
              : ""
          }
        </td>

        <td class="right">1</td>

        <td class="right">
          ${escapeHtml(
            formatMoney(bill.roomRent)
          )}
        </td>

        <td class="right">
          ${escapeHtml(
            formatMoney(bill.roomRent)
          )}
        </td>
      </tr>
    `,

    buildChargeRowsHtml({
      title: "Food Charges",
      items: foodItems,
    }),

    buildChargeRowsHtml({
      title: "Laundry Charges",
      items: laundryItems,
    }),

    buildChargeRowsHtml({
      title: "Extra Charges",
      items: extraItems,
    }),
  ]
    .filter(Boolean)
    .join("");

  return `
    <!DOCTYPE html>

    <html lang="en">
      <head>
        <meta charset="UTF-8" />

        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0"
        />

        <title>${escapeHtml(
          invoiceNumber
        )}</title>

        <style>
          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;
            padding: 10mm;
            background: #ffffff;
            color: #0f172a;
            font-family:
              Arial,
              Helvetica,
              sans-serif;
          }

          .invoice {
            width: 100%;
            max-width: 190mm;
            margin: auto;
          }

          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 20px;
            padding-bottom: 14px;
            border-bottom: 2px solid #0f172a;
          }

          .hotel-name {
            margin: 0;
            font-size: 25px;
            font-weight: 900;
            text-transform: uppercase;
          }

          .document-title {
            margin-top: 5px;
            color: #475569;
            font-size: 12px;
            font-weight: 800;
            letter-spacing: 1px;
          }

          .status {
            padding: 7px 12px;
            border-radius: 999px;
            background:
              ${paymentStatus.background};
            color:
              ${paymentStatus.color};
            font-size: 11px;
            font-weight: 900;
          }

          .details-grid {
            display: grid;
            grid-template-columns:
              repeat(2, minmax(0, 1fr));
            gap: 12px;
            margin-top: 15px;
          }

          .details-box {
            padding: 11px;
            border: 1px solid #cbd5e1;
          }

          .details-title {
            margin-bottom: 8px;
            color: #475569;
            font-size: 11px;
            font-weight: 900;
            text-transform: uppercase;
          }

          .detail-row {
            display: flex;
            justify-content: space-between;
            gap: 15px;
            padding: 3px 0;
            font-size: 11px;
          }

          .detail-row span:first-child {
            color: #64748b;
          }

          .detail-row strong {
            text-align: right;
            overflow-wrap: anywhere;
          }

          table {
            width: 100%;
            margin-top: 16px;
            border-collapse: collapse;
          }

          th,
          td {
            padding: 8px;
            border: 1px solid #cbd5e1;
            font-size: 10px;
            vertical-align: top;
          }

          th {
            background: #f1f5f9;
            font-weight: 900;
            text-align: left;
          }

          .right {
            text-align: right;
          }

          .category-row td {
            background: #f8fafc;
            color: #334155;
            font-weight: 900;
          }

          .item-note {
            margin-top: 3px;
            color: #64748b;
            font-size: 8px;
          }

          .total-area {
            width: 52%;
            margin-top: 14px;
            margin-left: auto;
          }

          .total-row {
            display: flex;
            justify-content: space-between;
            gap: 15px;
            padding: 5px 0;
            font-size: 11px;
          }

          .total-row.bold {
            padding-top: 8px;
            border-top: 1px solid #94a3b8;
            font-size: 14px;
            font-weight: 900;
          }

          .total-row.balance {
            color:
              ${
                toAmount(bill.balance) > 0
                  ? "#b91c1c"
                  : "#166534"
              };
            font-weight: 900;
          }

          .payment-section {
            margin-top: 18px;
          }

          .payment-title {
            margin-bottom: 7px;
            font-size: 12px;
            font-weight: 900;
          }

          .payment-grid {
            display: grid;
            grid-template-columns:
              repeat(2, minmax(0, 1fr));
            gap: 7px;
          }

          .payment-box {
            padding: 8px;
            border: 1px solid #e2e8f0;
            font-size: 9px;
          }

          .declaration {
            margin-top: 18px;
            padding: 10px;
            border: 1px solid #cbd5e1;
            color: #475569;
            font-size: 9px;
            line-height: 1.5;
          }

          .signature-grid {
            display: grid;
            grid-template-columns:
              repeat(2, minmax(0, 1fr));
            gap: 80px;
            margin-top: 48px;
          }

          .signature {
            text-align: center;
            font-size: 10px;
          }

          .signature-line {
            margin-bottom: 5px;
            border-top: 1px solid #111827;
          }

          .footer {
            margin-top: 15px;
            text-align: center;
            color: #64748b;
            font-size: 8px;
          }

          @media print {
            body {
              padding: 0;
            }

            .invoice {
              max-width: none;
            }

            @page {
              size: A4 portrait;
              margin: 9mm;
            }
          }
        </style>
      </head>

      <body>
        <main class="invoice">
          <header class="header">
            <div>
              <h1 class="hotel-name">
                ${escapeHtml(
                  hotelName || "Hotel"
                )}
              </h1>

              <div class="document-title">
                TAX INVOICE
              </div>
            </div>

            <div class="status">
              ${paymentStatus.text}
            </div>
          </header>

          <section class="details-grid">
            <div class="details-box">
              <div class="details-title">
                Invoice Details
              </div>

              <div class="detail-row">
                <span>Invoice No.</span>

                <strong>
                  ${escapeHtml(
                    invoiceNumber
                  )}
                </strong>
              </div>

              <div class="detail-row">
                <span>Invoice Date</span>

                <strong>
                  ${escapeHtml(
                    formatDateTime(
                      invoiceDate
                    )
                  )}
                </strong>
              </div>

              <div class="detail-row">
                <span>Room Number</span>

                <strong>
                  ${escapeHtml(
                    stay?.room || "-"
                  )}
                </strong>
              </div>

              <div class="detail-row">
                <span>A&D Serial</span>

                <strong>
                  ${escapeHtml(
                    stay?.adRegisterSerialNo ||
                      "-"
                  )}
                </strong>
              </div>

              <div class="detail-row">
                <span>Booking Source</span>

                <strong>
                  ${escapeHtml(
                    stay?.bookingSource ||
                      "-"
                  )}
                </strong>
              </div>

              <div class="detail-row">
                <span>Booking Reference</span>

                <strong>
                  ${escapeHtml(
                    getBookingReference(
                      stay
                    )
                  )}
                </strong>
              </div>
            </div>

            <div class="details-box">
              <div class="details-title">
                Guest Details
              </div>

              <div class="detail-row">
                <span>Guest Name</span>

                <strong>
                  ${escapeHtml(
                    primaryGuest?.name ||
                      stay?.guestName ||
                      "Guest"
                  )}
                </strong>
              </div>

              <div class="detail-row">
                <span>Phone Number</span>

                <strong>
                  ${escapeHtml(
                    primaryGuest?.phoneNumber ||
                      stay?.phoneNumber ||
                      "-"
                  )}
                </strong>
              </div>

              <div class="detail-row">
                <span>Address</span>

                <strong>
                  ${escapeHtml(
                    primaryGuest?.address ||
                      stay?.address ||
                      "-"
                  )}
                </strong>
              </div>

              <div class="detail-row">
                <span>Nationality</span>

                <strong>
                  ${escapeHtml(
                    primaryGuest?.nationality ||
                      stay?.nationality ||
                      "-"
                  )}
                </strong>
              </div>

              <div class="detail-row">
                <span>ID Type</span>

                <strong>
                  ${escapeHtml(
                    primaryGuest?.idType ||
                      stay?.idType ||
                      "-"
                  )}
                </strong>
              </div>
            </div>

            <div class="details-box">
              <div class="details-title">
                Stay Details
              </div>

              <div class="detail-row">
                <span>Check-In</span>

                <strong>
                  ${escapeHtml(
                    formatDateTime(
                      stay?.checkInAt
                    )
                  )}
                </strong>
              </div>

              <div class="detail-row">
                <span>Expected Checkout</span>

                <strong>
                  ${escapeHtml(
                    formatDateTime(
                      stay?.expectedCheckOutAt
                    )
                  )}
                </strong>
              </div>

              <div class="detail-row">
                <span>Actual Checkout</span>

                <strong>
                  ${escapeHtml(
                    formatDateTime(
                      stay?.actualCheckOutAt
                    )
                  )}
                </strong>
              </div>

              <div class="detail-row">
                <span>Stay Duration</span>

                <strong>
                  ${escapeHtml(
                    getStayDuration(stay)
                  )}
                </strong>
              </div>

              <div class="detail-row">
                <span>Adults / Children</span>

                <strong>
                  ${escapeHtml(
                    `${
                      stay?.adults ??
                      stay?.guestCount ??
                      1
                    } / ${
                      stay?.children ?? 0
                    }`
                  )}
                </strong>
              </div>
            </div>

            <div class="details-box">
              <div class="details-title">
                Payment Details
              </div>

              <div class="detail-row">
                <span>Initial Mode</span>

                <strong>
                  ${escapeHtml(
                    stay?.paymentMode ||
                      "-"
                  )}
                </strong>
              </div>

              <div class="detail-row">
                <span>Advance</span>

                <strong>
                  ${escapeHtml(
                    formatMoney(
                      stay?.advance
                    )
                  )}
                </strong>
              </div>

              <div class="detail-row">
                <span>Total Paid</span>

                <strong>
                  ${escapeHtml(
                    formatMoney(
                      bill?.paid
                    )
                  )}
                </strong>
              </div>

              <div class="detail-row">
                <span>Balance</span>

                <strong>
                  ${escapeHtml(
                    formatMoney(
                      bill?.balance
                    )
                  )}
                </strong>
              </div>
            </div>
          </section>

          <table>
            <thead>
              <tr>
                <th style="width:7%">
                  S.No.
                </th>

                <th>
                  Description
                </th>

                <th
                  class="right"
                  style="width:10%"
                >
                  Qty
                </th>

                <th
                  class="right"
                  style="width:17%"
                >
                  Rate
                </th>

                <th
                  class="right"
                  style="width:18%"
                >
                  Amount
                </th>
              </tr>
            </thead>

            <tbody>
              ${chargeRows}
            </tbody>
          </table>

          <section class="total-area">
            <div class="total-row">
              <span>Gross Total</span>

              <strong>
                ${escapeHtml(
                  formatMoney(
                    bill.grossTotal
                  )
                )}
              </strong>
            </div>

            <div class="total-row">
              <span>Discount</span>

              <strong>
                -${escapeHtml(
                  formatMoney(
                    bill.discount
                  )
                )}
              </strong>
            </div>

            <div class="total-row">
              <span>Taxable Amount</span>

              <strong>
                ${escapeHtml(
                  formatMoney(
                    bill.taxableAmount
                  )
                )}
              </strong>
            </div>

            ${
              toAmount(bill.gstRate) > 0
                ? `
                  <div class="total-row">
                    <span>
                      CGST (${toAmount(
                        bill.gstRate
                      ) / 2}%)
                    </span>

                    <strong>
                      ${escapeHtml(
                        formatMoney(
                          bill.cgst
                        )
                      )}
                    </strong>
                  </div>

                  <div class="total-row">
                    <span>
                      SGST (${toAmount(
                        bill.gstRate
                      ) / 2}%)
                    </span>

                    <strong>
                      ${escapeHtml(
                        formatMoney(
                          bill.sgst
                        )
                      )}
                    </strong>
                  </div>
                `
                : ""
            }

            ${bill.damageCharge > 0 ? `<div class="total-row"><span>Damage Charge</span><strong>${escapeHtml(formatMoney(bill.damageCharge))}</strong></div>` : ''}
            ${bill.excessPaid > 0 ? `<div class="total-row"><span>Excess received / Refund due</span><strong>${escapeHtml(formatMoney(bill.excessPaid))}</strong></div>` : ''}
            <div class="total-row bold">
              <span>Grand Total</span>

              <strong>
                ${escapeHtml(
                  formatMoney(
                    bill.grandTotal
                  )
                )}
              </strong>
            </div>

            <div class="total-row">
              <span>Total Paid</span>

              <strong>
                ${escapeHtml(
                  formatMoney(
                    bill.paid
                  )
                )}
              </strong>
            </div>

            <div class="total-row balance">
              <span>Balance</span>

              <strong>
                ${escapeHtml(
                  formatMoney(
                    bill.balance
                  )
                )}
              </strong>
            </div>
          </section>

          ${
            payments.length
              ? `
                <section class="payment-section">
                  <div class="payment-title">
                    Payment History
                  </div>

                  <div class="payment-grid">
                    ${payments
                      .map(
                        (payment) => `
                          <div class="payment-box">
                            <strong>
                              ${escapeHtml(
                                formatMoney(
                                  payment?.amount
                                )
                              )}
                            </strong>

                            <div>
                              ${escapeHtml(
                                payment?.paymentMode ||
                                  "-"
                              )}
                            </div>

                            <div>
                              ${escapeHtml(
                                formatDateTime(
                                  payment?.addedAt
                                )
                              )}
                            </div>

                            ${
                              escapePrintValue(clean(
                                payment?.transactionReference
                              ))
                                ? `<div>Ref: ${escapeHtml(
                                    payment.transactionReference
                                  )}</div>`
                                : ""
                            }
                          </div>
                        `
                      )
                      .join("")}
                  </div>
                </section>
              `
              : ""
          }

          <section class="declaration">
            This invoice has been generated from the
            Hotel PMS based on room tariff, food,
            laundry, extra charges, discounts,
            applicable GST and payments recorded for
            this stay. Please report any discrepancy
            to the reception before departure.
          </section>

          <section class="signature-grid">
            <div class="signature">
              <div class="signature-line"></div>
              Guest Signature
            </div>

            <div class="signature">
              <div class="signature-line"></div>
              Authorised Signatory
            </div>
          </section>

          <div class="footer">
            Computer-generated hotel invoice
          </div>
        </main>

        <script>
          window.onload = function () {
            setTimeout(function () {
              window.focus();
              window.print();
            }, 300);
          };
        </script>
      </body>
    </html>
  `;
};

/* -------------------------------------------------------------------------- */
/*                          Main Invoice Component                            */
/* -------------------------------------------------------------------------- */

function InvoiceModal({
  stay,
  bill,
  invoiceNumber,
  hotelName,
  onClose,
}) {
  const invoiceDate = useMemo(
    () =>
      stay?.actualCheckOutAt ||
      stay?.createdAtIso ||
      new Date().toISOString(),
    [
      stay?.actualCheckOutAt,
      stay?.createdAtIso,
    ]
  );

  const primaryGuest = useMemo(
    () => getPrimaryGuest(stay),
    [stay]
  );

  const paymentStatus =
    useMemo(
      () => getPaymentStatus(bill),
      [bill]
    );

  const foodItems = useMemo(
    () =>
      getItems(stay, "foodItems"),
    [stay]
  );

  const laundryItems = useMemo(
    () =>
      getItems(
        stay,
        "laundryItems"
      ),
    [stay]
  );

  const extraItems = useMemo(
    () =>
      getItems(stay, "extraItems"),
    [stay]
  );

  if (!stay || !bill) return null;

  const printInvoice = () => {
    const printWindow = window.open(
      "",
      "_blank",
      "width=1100,height=900"
    );

    if (!printWindow) {
      alert(
        "Print popup block ho gaya. Browser me popups allow karo."
      );

      return;
    }

    printWindow.document.open();

    printWindow.document.write(
      buildInvoiceHtml({
        stay,
        bill,
        hotelName:
          hotelName ||
          stay.hotelName ||
          "Hotel",

        invoiceNumber:
          invoiceNumber ||
          stay.invoiceNumber ||
          "Invoice",

        invoiceDate,
      })
    );

    printWindow.document.close();
  };

  return (
    <div
      style={overlayStyle}
      onMouseDown={(event) => {
        if (
          event.target ===
          event.currentTarget
        ) {
          onClose?.();
        }
      }}
    >
      <div style={modalStyle}>
        <div style={toolbarStyle}>
          <button
            type="button"
            onClick={() =>
              onClose?.()
            }
            style={secondaryButtonStyle}
          >
            Close
          </button>

          <button
            type="button"
            onClick={printInvoice}
            style={primaryButtonStyle}
          >
            🖨 Print / Save PDF
          </button>
        </div>

        <article style={invoiceSheetStyle}>
          <header style={invoiceHeaderStyle}>
            <div>
              <h1 style={hotelNameStyle}>
                {hotelName ||
                  stay.hotelName ||
                  "Hotel"}
              </h1>

              <div style={taxInvoiceStyle}>
                TAX INVOICE
              </div>
            </div>

            <span
              style={{
                ...statusBadgeStyle,

                background:
                  paymentStatus.background,

                color:
                  paymentStatus.color,
              }}
            >
              {paymentStatus.text}
            </span>
          </header>

          <section style={detailsGridStyle}>
            <DetailsCard
              title="Invoice Details"
              rows={[
                [
                  "Invoice No.",
                  invoiceNumber || "-",
                ],

                [
                  "Invoice Date",
                  formatDateTime(
                    invoiceDate
                  ),
                ],

                [
                  "Room",
                  stay.room || "-",
                ],

                [
                  "A&D Serial",
                  stay.adRegisterSerialNo ||
                    "-",
                ],
              ]}
            />

            <DetailsCard
              title="Guest Details"
              rows={[
                [
                  "Guest",
                  primaryGuest?.name ||
                    stay.guestName ||
                    "Guest",
                ],

                [
                  "Phone",
                  primaryGuest?.phoneNumber ||
                    stay.phoneNumber ||
                    "-",
                ],

                [
                  "Address",
                  primaryGuest?.address ||
                    stay.address ||
                    "-",
                ],

                [
                  "ID Type",
                  primaryGuest?.idType ||
                    stay.idType ||
                    "-",
                ],
              ]}
            />

            <DetailsCard
              title="Stay Details"
              rows={[
                [
                  "Check-In",
                  formatDateTime(
                    stay.checkInAt
                  ),
                ],

                [
                  "Expected Checkout",
                  formatDateTime(
                    stay.expectedCheckOutAt
                  ),
                ],

                [
                  "Stay Type",
                  getStayDuration(stay),
                ],

                [
                  "Booking Source",
                  stay.bookingSource ||
                    "-",
                ],
              ]}
            />

            <DetailsCard
              title="Payment Details"
              rows={[
                [
                  "Advance",
                  formatMoney(
                    stay.advance
                  ),
                ],

                [
                  "Total Paid",
                  formatMoney(
                    bill.paid
                  ),
                ],

                [
                  "Balance",
                  formatMoney(
                    bill.balance
                  ),
                ],

                [
                  "Mode",
                  stay.paymentMode ||
                    "-",
                ],
              ]}
            />
          </section>

          <section style={chargeSummaryStyle}>
            <ChargeCard
              label="Room Rent"
              value={bill.roomRent}
            />

            <ChargeCard
              label={`Food (${foodItems.length})`}
              value={bill.food}
            />

            <ChargeCard
              label={`Laundry (${laundryItems.length})`}
              value={bill.laundry}
            />

            <ChargeCard
              label={`Extra (${extraItems.length})`}
              value={bill.extra}
            />
          </section>

          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={tableHeaderStyle}>
                  Description
                </th>

                <th
                  style={
                    tableHeaderRightStyle
                  }
                >
                  Amount
                </th>
              </tr>
            </thead>

            <tbody>
              <InvoiceRow
                label="Room Rent"
                value={bill.roomRent}
              />

              <InvoiceRow
                label="Food Charges"
                value={bill.food}
              />

              <InvoiceRow
                label="Laundry Charges"
                value={bill.laundry}
              />

              <InvoiceRow
                label="Extra Charges"
                value={bill.extra}
              />

              {bill.damageCharge > 0 && <InvoiceRow label="Damage Charge" value={bill.damageCharge} />}
              {bill.excessPaid > 0 && <InvoiceRow label="Excess received / Refund due" value={bill.excessPaid} />}

              <InvoiceRow
                label="Gross Total"
                value={bill.grossTotal}
                bold
              />

              <InvoiceRow
                label="Discount"
                value={-bill.discount}
              />

              <InvoiceRow
                label="Taxable Amount"
                value={
                  bill.taxableAmount
                }
              />

              {toAmount(
                bill.gstRate
              ) > 0 && (
                <>
                  <InvoiceRow
                    label={`CGST (${
                      toAmount(
                        bill.gstRate
                      ) / 2
                    }%)`}
                    value={bill.cgst}
                  />

                  <InvoiceRow
                    label={`SGST (${
                      toAmount(
                        bill.gstRate
                      ) / 2
                    }%)`}
                    value={bill.sgst}
                  />
                </>
              )}

              <InvoiceRow
                label="Grand Total"
                value={bill.grandTotal}
                bold
              />

              <InvoiceRow
                label="Paid"
                value={bill.paid}
              />

              <InvoiceRow
                label="Balance"
                value={bill.balance}
                bold
                danger={
                  bill.balance > 0
                }
              />
            </tbody>
          </table>

          <section style={signatureAreaStyle}>
            <div style={signatureBoxStyle}>
              <div style={signatureLineStyle} />
              Guest Signature
            </div>

            <div style={signatureBoxStyle}>
              <div style={signatureLineStyle} />
              Authorised Signatory
            </div>
          </section>
        </article>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                             Small Components                               */
/* -------------------------------------------------------------------------- */

function DetailsCard({
  title,
  rows,
}) {
  return (
    <div style={detailsCardStyle}>
      <div style={detailsTitleStyle}>
        {title}
      </div>

      {rows.map(
        ([label, value]) => (
          <div
            key={label}
            style={detailsRowStyle}
          >
            <span style={detailsLabelStyle}>
              {label}
            </span>

            <strong style={detailsValueStyle}>
              {value}
            </strong>
          </div>
        )
      )}
    </div>
  );
}

function ChargeCard({
  label,
  value,
}) {
  return (
    <div style={chargeCardStyle}>
      <div style={chargeLabelStyle}>
        {label}
      </div>

      <strong style={chargeValueStyle}>
        {formatMoney(value)}
      </strong>
    </div>
  );
}

function InvoiceRow({
  label,
  value,
  bold = false,
  danger = false,
}) {
  return (
    <tr>
      <td
        style={{
          ...tableCellStyle,

          fontWeight: bold
            ? 900
            : 500,

          color: danger
            ? "#b91c1c"
            : "#334155",
        }}
      >
        {label}
      </td>

      <td
        style={{
          ...tableCellRightStyle,

          fontWeight: bold
            ? 900
            : 700,

          color: danger
            ? "#b91c1c"
            : "#0f172a",
        }}
      >
        {formatMoney(value)}
      </td>
    </tr>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 22000,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  padding: 12,

  background:
    "rgba(15,23,42,.74)",
};

const modalStyle = {
  width: "min(950px,100%)",
  maxHeight: "96vh",

  overflow: "auto",

  padding: 14,

  borderRadius: 18,

  background: "#e2e8f0",
};

const toolbarStyle = {
  position: "sticky",
  top: 0,
  zIndex: 10,

  display: "flex",
  justifyContent: "flex-end",

  gap: 10,

  marginBottom: 12,
};

const invoiceSheetStyle = {
  minHeight: 700,

  padding: 30,

  boxSizing: "border-box",

  background: "#ffffff",
  color: "#0f172a",

  boxShadow:
    "0 8px 30px rgba(15,23,42,.12)",
};

const invoiceHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",

  gap: 20,

  paddingBottom: 14,

  borderBottom:
    "2px solid #0f172a",
};

const hotelNameStyle = {
  margin: 0,

  color: "#0f172a",

  fontSize: 25,
  textTransform: "uppercase",
};

const taxInvoiceStyle = {
  marginTop: 5,

  color: "#475569",

  fontSize: 11,
  fontWeight: 900,
  letterSpacing: 1,
};

const statusBadgeStyle = {
  padding: "7px 12px",

  borderRadius: 999,

  fontSize: 10,
  fontWeight: 900,
};

const detailsGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(230px,1fr))",

  gap: 11,

  marginTop: 15,
};

const detailsCardStyle = {
  padding: 11,

  border:
    "1px solid #cbd5e1",
};

const detailsTitleStyle = {
  marginBottom: 8,

  color: "#475569",

  fontSize: 10,
  fontWeight: 900,

  textTransform: "uppercase",
};

const detailsRowStyle = {
  display: "flex",
  justifyContent: "space-between",

  gap: 14,

  padding: "3px 0",
};

const detailsLabelStyle = {
  color: "#64748b",
  fontSize: 10,
};

const detailsValueStyle = {
  color: "#334155",

  fontSize: 10,
  textAlign: "right",
  overflowWrap: "anywhere",
};

const chargeSummaryStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(120px,1fr))",

  gap: 8,

  marginTop: 14,
};

const chargeCardStyle = {
  padding: 10,

  borderRadius: 10,

  background: "#f8fafc",

  border:
    "1px solid #e2e8f0",

  textAlign: "center",
};

const chargeLabelStyle = {
  color: "#64748b",

  fontSize: 9,
  fontWeight: 800,
};

const chargeValueStyle = {
  display: "block",

  marginTop: 4,

  fontSize: 14,
};

const tableStyle = {
  width: "100%",

  marginTop: 16,

  borderCollapse: "collapse",
};

const tableHeaderStyle = {
  padding: 9,

  border:
    "1px solid #cbd5e1",

  background: "#f1f5f9",

  textAlign: "left",

  fontSize: 11,
};

const tableHeaderRightStyle = {
  ...tableHeaderStyle,
  textAlign: "right",
};

const tableCellStyle = {
  padding: 9,

  border:
    "1px solid #cbd5e1",

  fontSize: 11,
};

const tableCellRightStyle = {
  ...tableCellStyle,
  textAlign: "right",
};

const signatureAreaStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(2,minmax(0,1fr))",

  gap: 80,

  marginTop: 55,
};

const signatureBoxStyle = {
  color: "#475569",

  fontSize: 10,
  textAlign: "center",
};

const signatureLineStyle = {
  marginBottom: 5,

  borderTop:
    "1px solid #111827",
};

const commonButtonStyle = {
  border: 0,

  borderRadius: 10,

  padding: "10px 14px",

  color: "#ffffff",

  fontWeight: 800,
  cursor: "pointer",
};

const primaryButtonStyle = {
  ...commonButtonStyle,
  background: "#16a34a",
};

const secondaryButtonStyle = {
  ...commonButtonStyle,
  background: "#475569",
};

export default InvoiceModal;